import { TimeOvertimeRequestStatus } from '@/generated/graphql/types';

import type { TMyOvertimeRequest } from '../../types';

// QA/demo fixtures for `?force=solicitudes[:variant]`, read through the
// shared `useForcedUi` convention. OFF BY DEFAULT: with no URL parameter
// nothing here runs and `useMyRequests` behaves exactly as before.
//
// Why this exists: QA needs every visible state of this screen — empty,
// loading, error, a full page, and the two pagination bugs this branch
// fixed — with no dependency on real data in the qa organization.
// Read-only and visual only: cancelling a forced row never reaches the
// network. It only drops the row from the small store below.

type TForcedMyRequestsResult = {
  items: TMyOvertimeRequest[];
  totalCount: number;
  loading: boolean;
  error: boolean;
};

const KNOWN_VARIANTS = new Set(['', 'empty', 'loading', 'error', 'paginated']);

export const isKnownForcedMyRequestsVariant = (
  variant: string | undefined,
): boolean => variant !== undefined && KNOWN_VARIANTS.has(variant);

// --- Removable local state --------------------------------------------------
//
// A forced row has no real id the mutation can act on, so cancelling one
// must never call `cancel_overtime_request`. `useCancelOvertimeRequest`
// calls `removeForcedMyRequest` directly instead, and `useMyRequests`
// subscribes through `useSyncExternalStore` so the table re-renders with
// the row gone. The snapshot is the version number, a primitive, not the
// set itself — a mutated-in-place object would look unchanged to React.

let version = 0;
let listeners: Array<() => void> = [];
const removedIds = new Set<string>();

export const removeForcedMyRequest = (id: string): void => {
  removedIds.add(id);
  version += 1;
  listeners.forEach(listener => listener());
};

export const subscribeToForcedMyRequests = (
  listener: () => void,
): (() => void) => {
  listeners = [...listeners, listener];
  return () => {
    listeners = listeners.filter(current => current !== listener);
  };
};

export const getForcedMyRequestsVersion = (): number => version;

// --- Fixture data ------------------------------------------------------------

const buildRequest = (
  overrides: Partial<TMyOvertimeRequest> & { id: string },
): TMyOvertimeRequest => ({
  __typename: 'TimeOvertimeRequest',
  centralized_organization_id: 21,
  user_id: 1001,
  request_date: '2026-09-01',
  shift_id: 'shift-1',
  shift_name: 'Turno mañana',
  extra_minutes: 120,
  replacement_minutes: 120,
  reason: 'Cierre de mes contable.',
  status: TimeOvertimeRequestStatus.Pending,
  compensation_check_deferred: false,
  final_extra_minutes: null,
  final_replacement_minutes: null,
  has_final_amount_difference: false,
  created_at: '2026-09-01T09:00:00.000Z',
  ...overrides,
});

const LONG_REASON =
  'Se necesitó apoyo adicional para el cierre financiero de fin de mes, porque el equipo contable recibió una carga de trabajo mayor a la esperada y fue necesario extender la jornada para entregar los reportes fiscales antes de la fecha límite que fijó la gerencia general.';

// Bare `?force=solicitudes` — every visible status, the difference icon, a
// long reason, and a row with no shift.
const FULL_DATASET: TMyOvertimeRequest[] = [
  buildRequest({
    id: 'forced-solicitud-1',
    request_date: '2026-09-02',
    shift_name: 'Turno mañana',
    reason: 'Cierre de inventario mensual.',
    status: TimeOvertimeRequestStatus.Pending,
  }),
  buildRequest({
    id: 'forced-solicitud-2',
    request_date: '2026-09-05',
    shift_name: 'Turno tarde',
    reason: 'Soporte a la campaña de fin de mes.',
    status: TimeOvertimeRequestStatus.Approved,
    extra_minutes: 180,
    replacement_minutes: 180,
    final_extra_minutes: 120,
    final_replacement_minutes: 120,
    has_final_amount_difference: true,
  }),
  buildRequest({
    id: 'forced-solicitud-3',
    request_date: '2026-09-08',
    shift_name: 'Turno noche',
    reason: 'Cobertura por ausencia de un compañero.',
    status: TimeOvertimeRequestStatus.Denied,
  }),
  buildRequest({
    id: 'forced-solicitud-4',
    request_date: '2026-09-10',
    shift_id: null,
    shift_name: null,
    reason: 'Trabajo remoto fuera del turno asignado.',
    status: TimeOvertimeRequestStatus.Pending,
  }),
  buildRequest({
    id: 'forced-solicitud-5',
    request_date: '2026-09-12',
    shift_name: 'Turno mañana',
    reason: LONG_REASON,
    status: TimeOvertimeRequestStatus.Approved,
  }),
  buildRequest({
    id: 'forced-solicitud-6',
    request_date: '2026-09-14',
    shift_name: 'Turno tarde',
    reason: 'Preparación de material para capacitación.',
    status: TimeOvertimeRequestStatus.Pending,
  }),
];

// `?force=solicitudes:paginated` — 41 rows, PAGE_SIZE 8. Page 6 holds
// exactly one row (bug B: cancelling it must not read as "no requests at
// all"). Page 2 at size 25 holds data too (bug A: the wrong landing page
// after a page-size change must be visible, not an empty screen).
const PAGINATED_STATUS_CYCLE = [
  TimeOvertimeRequestStatus.Pending,
  TimeOvertimeRequestStatus.Approved,
  TimeOvertimeRequestStatus.Denied,
];

const PAGINATED_ROW_COUNT = 41;

const buildPaginatedDataset = (): TMyOvertimeRequest[] =>
  Array.from({ length: PAGINATED_ROW_COUNT }, (_unused, index) => {
    const rowNumber = index + 1;
    const day = String((index % 28) + 1).padStart(2, '0');
    // Row 41 stays PENDING — it is the exact row bug B cancels.
    const status =
      rowNumber === PAGINATED_ROW_COUNT
        ? TimeOvertimeRequestStatus.Pending
        : PAGINATED_STATUS_CYCLE[index % PAGINATED_STATUS_CYCLE.length];

    return buildRequest({
      id: `forced-paginated-${rowNumber}`,
      request_date: `2026-08-${day}`,
      shift_id: `shift-${rowNumber}`,
      shift_name: `Turno ${rowNumber}`,
      reason: `Solicitud de horas extra número ${rowNumber}.`,
      status,
      created_at: `2026-08-${day}T09:00:00.000Z`,
    });
  });

const PAGINATED_DATASET = buildPaginatedDataset();

const paginate = (
  items: TMyOvertimeRequest[],
  page: number,
  pageSize: number,
): TForcedMyRequestsResult => {
  const visible = items.filter(item => !removedIds.has(item.id));
  const start = (page - 1) * pageSize;

  return {
    items: visible.slice(start, start + pageSize),
    totalCount: visible.length,
    loading: false,
    error: false,
  };
};

/**
 * Maps the `?force=solicitudes[:variant]` token to a forced result.
 *
 * `undefined` in (no token, or a variant this map does not know) →
 * `undefined` out, so the caller keeps its real data path. An unknown
 * variant falls through exactly like the absent param, on purpose.
 */
export const resolveForcedMyRequests = (
  variant: string | undefined,
  page: number,
  pageSize: number,
): TForcedMyRequestsResult | undefined => {
  if (variant === undefined || !KNOWN_VARIANTS.has(variant)) {
    return undefined;
  }
  if (variant === 'empty') {
    return { items: [], totalCount: 0, loading: false, error: false };
  }
  if (variant === 'loading') {
    return { items: [], totalCount: 0, loading: true, error: false };
  }
  if (variant === 'error') {
    return { items: [], totalCount: 0, loading: false, error: true };
  }
  if (variant === 'paginated') {
    return paginate(PAGINATED_DATASET, page, pageSize);
  }

  return paginate(FULL_DATASET, page, pageSize);
};
